import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Upload, FileSpreadsheet, CheckCircle, AlertCircle,
  Loader2, Home, User, Users, RefreshCw, FileText, Database,
  Building2, BedDouble, PoundSterling, Shield, ArrowRight
} from "lucide-react";
import { API } from "@/App";

export const BulkImportPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(true);
  
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(null);
      setResult(null);
    }
  };
  
  const handlePreview = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }
    
    setPreviewing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/lettings/bulk-import/preview`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      setPreview(response.data);
      toast.success("File preview generated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview file");
    } finally {
      setPreviewing(false);
    }
  };
  
  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }
    
    setImporting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/lettings/bulk-import/execute?update_existing=${updateExisting}`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      setResult(response.data.stats);
      toast.success("Import completed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };
  
  const resetImport = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Bulk Import from 10/90</h1>
                <p className="text-xs text-muted-foreground">Import properties, landlords & tenants with all fields</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Import Complete */}
        {result && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                Import Complete
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg border">
                  <div className="flex items-center gap-2 text-blue-600 mb-2">
                    <Building2 className="w-5 h-5" />
                    <span className="font-medium">Properties</span>
                  </div>
                  <p className="text-2xl font-bold">{result.properties_created + result.properties_updated}</p>
                  <p className="text-sm text-muted-foreground">
                    {result.properties_created} new, {result.properties_updated} updated
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border">
                  <div className="flex items-center gap-2 text-purple-600 mb-2">
                    <User className="w-5 h-5" />
                    <span className="font-medium">Landlords</span>
                  </div>
                  <p className="text-2xl font-bold">{result.landlords_created + result.landlords_updated}</p>
                  <p className="text-sm text-muted-foreground">
                    {result.landlords_created} new, {result.landlords_updated} updated
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border">
                  <div className="flex items-center gap-2 text-emerald-600 mb-2">
                    <Users className="w-5 h-5" />
                    <span className="font-medium">Tenants</span>
                  </div>
                  <p className="text-2xl font-bold">{result.tenants_created + result.tenants_updated}</p>
                  <p className="text-sm text-muted-foreground">
                    {result.tenants_created} new, {result.tenants_updated} updated
                  </p>
                </div>
              </div>
              
              {/* Fields Updated */}
              {result.fields_updated && (
                <div className="bg-white p-4 rounded-lg border mb-4">
                  <p className="font-medium mb-2">Enhanced Fields Imported:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(result.fields_updated).map(([field, count]) => (
                      count > 0 && (
                        <Badge key={field} variant="secondary">
                          {field.replace('_', ' ')}: {count}
                        </Badge>
                      )
                    ))}
                  </div>
                </div>
              )}
              
              {result.errors && result.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <p className="font-medium text-red-700 mb-2">Errors ({result.errors.length}):</p>
                  <ul className="text-sm text-red-600 list-disc list-inside">
                    {result.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {result.errors.length > 5 && (
                      <li>...and {result.errors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}
              
              <div className="flex gap-3 mt-4">
                <Button onClick={resetImport}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Import Another File
                </Button>
                <Button variant="outline" onClick={() => navigate("/admin/lettings")}>
                  View Lettings
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* File Upload */}
        {!result && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Upload Section */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Upload className="w-5 h-5 text-blue-600" />
                    Upload 10/90 Export
                  </CardTitle>
                  <CardDescription>
                    Export your data from 10/90 and upload here to sync all fields
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      file ? 'border-green-300 bg-green-50' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xls,.xlsx,.csv,.html"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {file ? (
                      <>
                        <FileSpreadsheet className="w-12 h-12 mx-auto text-green-500 mb-3" />
                        <p className="font-medium text-green-700">{file.name}</p>
                        <p className="text-sm text-green-600">{(file.size / 1024).toFixed(1)} KB</p>
                        <Button variant="link" className="mt-2" onClick={(e) => { e.stopPropagation(); resetImport(); }}>
                          Choose different file
                        </Button>
                      </>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p className="font-medium">Drop your 10/90 export here</p>
                        <p className="text-sm text-muted-foreground">or click to browse</p>
                        <p className="text-xs text-muted-foreground mt-2">Supports .xls, .xlsx, .csv files</p>
                      </>
                    )}
                  </div>
                  
                  {/* Options */}
                  <div className="flex items-center gap-2 mt-4 p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      id="update-existing"
                      checked={updateExisting}
                      onCheckedChange={setUpdateExisting}
                    />
                    <Label htmlFor="update-existing" className="text-sm cursor-pointer">
                      Update existing records with new data (recommended)
                    </Label>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-3 mt-4">
                    <Button 
                      onClick={handlePreview} 
                      disabled={!file || previewing}
                      variant="outline"
                      className="flex-1"
                    >
                      {previewing ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <FileText className="w-4 h-4 mr-2" />
                      )}
                      Preview Data
                    </Button>
                    <Button 
                      onClick={handleImport} 
                      disabled={!file || importing}
                      className="flex-1"
                    >
                      {importing ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Database className="w-4 h-4 mr-2" />
                      )}
                      Import Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* Instructions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">How to Export from 10/90</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">1</div>
                    <div>
                      <p className="font-medium">Open 10/90</p>
                      <p className="text-muted-foreground">Go to Reports → Lettings → All Tenancies</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">2</div>
                    <div>
                      <p className="font-medium">Select All Fields</p>
                      <p className="text-muted-foreground">Include: Property details, EPC, Landlord, Tenant, Deposit info</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">3</div>
                    <div>
                      <p className="font-medium">Export as Excel</p>
                      <p className="text-muted-foreground">Click Export → Excel (.xls)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">4</div>
                    <div>
                      <p className="font-medium">Upload Here</p>
                      <p className="text-muted-foreground">Drop the file above to sync all data</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Preview Section */}
            <div className="space-y-6">
              {preview ? (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        File Preview
                      </CardTitle>
                      <CardDescription>
                        Found {preview.row_count} rows with {preview.columns_found?.length || 0} columns
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {/* Detected Mappings */}
                      <div className="space-y-4">
                        {/* Property Fields */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-sm">Property Fields</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {preview.field_mappings?.property && Object.entries(preview.field_mappings.property).map(([field, col]) => (
                              col && (
                                <Badge key={field} variant={col ? "default" : "secondary"} className="text-xs">
                                  {field}: ✓
                                </Badge>
                              )
                            ))}
                            {Object.values(preview.field_mappings?.property || {}).filter(v => v).length === 0 && (
                              <Badge variant="secondary" className="text-xs">No property fields detected</Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Landlord Fields */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <User className="w-4 h-4 text-purple-600" />
                            <span className="font-medium text-sm">Landlord Fields</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {preview.field_mappings?.landlord && Object.entries(preview.field_mappings.landlord).map(([field, col]) => (
                              col && (
                                <Badge key={field} variant={col ? "default" : "secondary"} className="text-xs">
                                  {field}: ✓
                                </Badge>
                              )
                            ))}
                          </div>
                        </div>
                        
                        {/* Tenant Fields */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-emerald-600" />
                            <span className="font-medium text-sm">Tenant Fields</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {preview.field_mappings?.tenant && Object.entries(preview.field_mappings.tenant).map(([field, col]) => (
                              col && (
                                <Badge key={field} variant={col ? "default" : "secondary"} className="text-xs">
                                  {field}: ✓
                                </Badge>
                              )
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Sample Data */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Sample Data (First 5 Rows)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-100">
                            <tr>
                              {preview.columns_found?.slice(0, 6).map((col, i) => (
                                <th key={i} className="px-2 py-1 text-left font-medium truncate max-w-[150px]">{col}</th>
                              ))}
                              {preview.columns_found?.length > 6 && (
                                <th className="px-2 py-1 text-left font-medium">+{preview.columns_found.length - 6} more</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {preview.sample_data?.map((row, idx) => (
                              <tr key={idx} className="border-b">
                                {preview.columns_found?.slice(0, 6).map((col, i) => (
                                  <td key={i} className="px-2 py-1 truncate max-w-[150px]" title={String(row[col] || '')}>
                                    {String(row[col] || '-').substring(0, 30)}
                                  </td>
                                ))}
                                {preview.columns_found?.length > 6 && (
                                  <td className="px-2 py-1 text-muted-foreground">...</td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* All Columns */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">All Columns Found</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-1">
                        {preview.columns_found?.map((col, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{col}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card className="h-full flex items-center justify-center min-h-[400px]">
                  <CardContent className="text-center py-12">
                    <FileText className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                    <p className="text-muted-foreground">Upload a file and click "Preview Data" to see what will be imported</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
        
        {/* Fields Supported */}
        {!result && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                Fields Supported
              </CardTitle>
              <CardDescription>
                All these fields from 10/90 will be mapped to All Hours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold">Property</span>
                  </div>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>✓ Address & Postcode</li>
                    <li>✓ Bedrooms, Bathrooms, Receptions</li>
                    <li>✓ Property Type & Furnished</li>
                    <li>✓ Floor Area, Garden, Parking</li>
                    <li>✓ Tenure (Freehold/Leasehold)</li>
                    <li>✓ EPC Rating & Expiry</li>
                    <li>✓ Council Tax Band</li>
                    <li>✓ Local Authority</li>
                    <li>✓ HMO Status</li>
                  </ul>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold">Landlord</span>
                  </div>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>✓ Name & Email</li>
                    <li>✓ Phone & Mobile</li>
                    <li>✓ Address</li>
                  </ul>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold">Tenant</span>
                  </div>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>✓ Name, Email, Phone</li>
                    <li>✓ Rent Amount</li>
                    <li>✓ Deposit Amount</li>
                    <li>✓ Deposit Scheme (DPS/TDS/etc)</li>
                    <li>✓ Start & End Date</li>
                    <li>✓ Status & Periodic</li>
                    <li>✓ Tenancy Reference</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default BulkImportPage;
